import React, { useEffect, useMemo, useState } from "react";
import { allocateProjectTargets } from "./utils/vendorTargetAllocation";
import axios from "axios";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import "./VendorPODetail.css";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

// Rates are user-entered. Neither tax applies until its checkbox is selected.
// This is a PO-level display/calculation; validate applicability and rates with Accounts.
const taxMoney = (value) => Math.round(Number(value || 0) * 100);
const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const positiveMoney = (...values) => {
  for (const value of values) {
    const amount = Number(value);
    if (Number.isFinite(amount) && amount > 0) return amount;
  }
  return 0;
};

// Use the approved quote's line selling value only when no saved target exists.
// Prefer an explicit line amount; calculate quantity × rate only as a fallback.
const approvedQuoteLineAmount = (item = {}) => {
  const explicit = positiveMoney(
    item.quoteAmount,
    item.totalAmount,
    item.total,
    item.amount,
    item.MRP,
    item.mrp,
  );
  if (explicit > 0) return explicit;
  const quantity = Number(item.qty ?? item.quantity ?? 1);
  const rate = positiveMoney(
    item.rate,
    item.unitRate,
    item.unitPrice,
    item.price,
  );
  return quantity > 0 && rate > 0 ? Math.round(quantity * rate * 100) / 100 : 0;
};
// Identify complimentary items only when the approved quote
// explicitly records a zero selling amount.
const isComplimentaryQuoteItem = (item = {}) => {
  const amountFields = [
    "quoteAmount",
    "totalAmount",
    "total",
    "amount",
    "price",
    "MRP",
    "mrp",
    "rate",
  ];

  for (const field of amountFields) {
    const value = item[field];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      const amount = Number(value);

      if (Number.isFinite(amount)) {
        return amount === 0;
      }
    }
  }

  return false;
};
const formatDifference = (value) => {
  const amount = Number(value || 0);

  const formatted = Math.abs(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (amount > 0) return `+₹${formatted}`;
  if (amount < 0) return `−₹${formatted}`;

  return `₹${formatted}`;
};
// Difference percentage is calculated against the target cost.
// A zero target has no meaningful percentage.
const formatDifferenceWithPercent = (difference, target) => {
  const base = Number(target || 0);
  const value = Number(difference || 0);
  if (!Number.isFinite(base) || base === 0) return formatDifference(value);
  const percent = (value / Math.abs(base)) * 100;
  const signedPercent = percent > 0 ? "+" : percent < 0 ? "−" : "";
  return `${formatDifference(value)} (${signedPercent}${Math.abs(percent).toFixed(2)}%)`;
};

const calculateTaxes = (items, gstEnabled, gstRate, tdsEnabled, tdsRate) => {
  const baseCents = (items || []).reduce(
    (sum, item) => sum + taxMoney(item.vendorAmount),
    0,
  );
  const gstCents = gstEnabled
    ? Math.round((baseCents * Number(gstRate || 0)) / 100)
    : 0;
  const tdsCents = tdsEnabled
    ? Math.round((baseCents * Number(tdsRate || 0)) / 100)
    : 0;
  return {
    baseAmount: baseCents / 100,
    gstAmount: gstCents / 100,
    tdsAmount: tdsCents / 100,
    netPayable: (baseCents + gstCents - tdsCents) / 100,
  };
};

/* ============================================================
   AUTH
============================================================ */

// Read the token from ONE key. Login must save the token under "token".
// Do not fall back to authToken/userToken/adminToken: they can contain tokens
// issued by a different backend or using a previous JWT signing secret.
const getAuthToken = () => localStorage.getItem("token") || "";

const getAuthConfig = () => {
  const token = getAuthToken();
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

/* ============================================================
   API ERROR
============================================================ */

const logApiError = (label, err) => {
  console.error(label, {
    url: err?.config?.url,
    method: err?.config?.method,
    status: err?.response?.status,
    data: err?.response?.data,
    message: err?.message,
  });
};

/* ============================================================
   COMPONENT
============================================================ */

const VendorPODetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vendorId } = useParams();

  const project = location.state?.project || null;

  const passedVendor = location.state?.vendor || null;

  const isNewPO = vendorId === "new";

  /* ==========================================================
     STATES
  ========================================================== */

  const [vendors, setVendors] = useState([]);

  const [vendor, setVendor] = useState(passedVendor || null);

  const [approvedItems, setApprovedItems] = useState([]);

  const [selectedItemIndex, setSelectedItemIndex] = useState("");

  const [poRows, setPoRows] = useState([]);

  const [savedPOId, setSavedPOId] = useState(null);
  const [projectPOs, setProjectPOs] = useState([]);
  const [allocationNotice, setAllocationNotice] = useState("");
  const [itemSyncing, setItemSyncing] = useState(false);
  const [gstEnabled, setGstEnabled] = useState(false);
  const [gstRate, setGstRate] = useState("");
  const [tdsEnabled, setTdsEnabled] = useState(false);
  const [tdsRate, setTdsRate] = useState("");
  const [sendingQuotePdf, setSendingQuotePdf] = useState(false);
  const [downloadingPoPdf, setDownloadingPoPdf] = useState(false);

  /* ----------------------------------------------------------
     DOCUMENT STATES
  ---------------------------------------------------------- */

  const [savedDocuments, setSavedDocuments] = useState([]);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [authError, setAuthError] = useState("");

  // An invalid signature cannot be fixed on the client. Show one inline notice
  // instead of repeated alerts, and keep unsaved PO rows in React state.
  const handleAuthError = (err) => {
    if (err?.response?.status !== 401) return false;

    const serverCode = err?.response?.data?.code;
    const sentAuth = err?.config?.headers?.Authorization || "";
    const currentToken = getAuthToken();

    // Do not delete a newer token if the user signed in in another tab
    // while this request was in flight.
    if (currentToken && sentAuth === `Bearer ${currentToken}`) {
      localStorage.removeItem("token");
    }

    setAuthError(
      serverCode === "TOKEN_EXPIRED"
        ? "Your login session expired. Sign in again in ERP, then retry. Your current PO entries are still on this page."
        : "The API rejected your login token. Sign in again using the same API, then retry. Your current PO entries are still on this page.",
    );
    return true;
  };

  const retryAfterLogin = () => {
    if (!getAuthToken()) {
      setAuthError(
        'No valid login token is stored. Sign in again in ERP, then click "Retry". Do not refresh this page if you have unsaved PO items.',
      );
      return;
    }

    setAuthError("");
    fetchAllVendors();
    if (project?.name) {
      fetchVendor();
      fetchApprovedQuoteItems();
      if (!isNewPO) fetchSavedVendorPO();
      else fetchProjectPOs();
    }
  };

  /* ==========================================================
     INITIAL LOAD
  ========================================================== */

  useEffect(() => {
    if (!getAuthToken()) {
      setAuthError(
        'No login token is stored for this ERP. Sign in again, then click "Retry" below. Do not refresh if you have unsaved PO entries.',
      );
      return;
    }

    fetchAllVendors();

    if (project?.name) {
      fetchVendor();
      fetchApprovedQuoteItems();

      if (!isNewPO) {
        fetchSavedVendorPO();
      } else {
        fetchProjectPOs();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.name, vendorId]);

  // Keep other open vendor quotes in sync without overwriting unsaved
  // vendor rates / quantities on this page. Re-fetch on focus and every 7 sec.
  useEffect(() => {
    const projectName = String(
      project?.name || project?.projectName || "",
    ).trim();
    if (!projectName) return undefined;
    let active = true;
    let pending = false;
    const refreshTargets = async () => {
      if (!active || pending || document.hidden || !getAuthToken()) return;
      pending = true;
      try {
        const [response, quoteResponse] = await Promise.all([
          axios.get(
            `${BASE_URL}/api/vendor-po?projectName=${encodeURIComponent(projectName)}`,
            getAuthConfig(),
          ),
          axios.get(
            `${BASE_URL}/api/quotes?projectName=${encodeURIComponent(projectName)}`,
            getAuthConfig(),
          ),
        ]);
        if (!active) return;
        const latest = (
          Array.isArray(quoteResponse.data) ? quoteResponse.data : []
        )
          .filter(
            (q) =>
              String(q.status || "")
                .trim()
                .toLowerCase() === "approved",
          )
          .sort(
            (a, b) =>
              new Date(b.updatedAt || b.createdAt || 0) -
              new Date(a.updatedAt || a.createdAt || 0),
          )[0];
        // On a quote revision, refresh the source dropdown and quantities.
        // Never create PO rows automatically from the approved quote.
        setApprovedItems((old) =>
          (latest?.items || []).map((item, index) => {
            const itemName = item.label || item.name || item.itemName || "";
            const previous = old.find(
              (entry) =>
                String(entry.itemName || "")
                  .trim()
                  .toLowerCase() === String(itemName).trim().toLowerCase(),
            );
            const isComplimentary = isComplimentaryQuoteItem(item);

            const actualTarget = isComplimentary
              ? 0
              : positiveMoney(
                  item.originalTargetCost,
                  item.vendorTargetCost,
                  item.vendorCost,
                  item.venderCost,
                  item.targetCost,
                  previous?.originalTargetCost,
                  previous?.vendorTargetCost,
                  Math.round(approvedQuoteLineAmount(item) * 0.5 * 100) / 100,
                );
            return {
              id: index,
              itemName,
              so:
                latest.quoteNo ||
                latest.soNumber ||
                latest.salesOrderNo ||
                `SO-${String(latest._id || "").slice(-6)}`,
              qty: item.qty || item.quantity || 1,
              originalTargetCost: actualTarget,
              vendorTargetCost: actualTarget,
              isComplimentary,
              targetMissing: isComplimentary ? false : actualTarget <= 0,
            };
          }),
        );
        if (Array.isArray(response.data)) {
          setProjectPOs(response.data);
          const currentVendorId = String(
            vendorId === "new"
              ? vendor?._id || vendor?.id || vendor?.vendorId || ""
              : vendorId,
          );
          const currentPO = response.data.find(
            (po) => String(po.vendorId) === currentVendorId,
          );
          if (currentPO && currentVendorId) {
            setAllocationNotice(""); // Clears notices about previously removed quote items.
            // Server is authoritative for item membership, SO and target. Keep
            // locally typed vendor Qty/Rate for rows that still exist.
            setPoRows((old) => {
              return (currentPO.items || []).map((row) => {
                const previous = old.find(
                  (entry) =>
                    String(entry.itemName || "")
                      .trim()
                      .toLowerCase() ===
                    String(row.itemName || "")
                      .trim()
                      .toLowerCase(),
                );
                const approved = (latest?.items || []).find(
                  (item) =>
                    normalizeText(item.label || item.name || item.itemName) ===
                    normalizeText(row.itemName),
                );
                const complimentary = approved
                  ? isComplimentaryQuoteItem(approved)
                  : row.isComplimentary === true;
                const correctedRow = complimentary
                  ? {
                      ...row,
                      isComplimentary: true,
                      targetMissing: false,
                      originalTargetCost: 0,
                      vendorTargetCost: 0,
                      targetCost: 0,
                    }
                  : row;
                return previous
                  ? {
                      ...correctedRow,
                      vendorQty: previous.vendorQty,
                      vendorRate: previous.vendorRate,
                      vendorAmount:
                        Number(previous.vendorQty || 0) *
                        Number(previous.vendorRate || 0),
                    }
                  : correctedRow;
              });
            });
          } else if (currentVendorId && !isNewPO) {
            setPoRows([]);
            setAllocationNotice("");
          }
        }
      } catch (err) {
        if (active) handleAuthError(err);
      } finally {
        pending = false;
      }
    };
    const onVisible = () => {
      if (!document.hidden) void refreshTargets();
    };
    window.addEventListener("focus", refreshTargets);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(refreshTargets, 7000);
    void refreshTargets();
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refreshTargets);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.name, project?.projectName, vendorId, vendor?._id]);

  /* ==========================================================
     HELPERS
  ========================================================== */

  const shortText = (text, limit = 70) => {
    if (!text) return "";

    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const normalizeText = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const getCurrentProjectName = () =>
    String(
      project?.name ||
        project?.projectName ||
        location.state?.projectName ||
        localStorage.getItem("selectedProjectName") ||
        "",
    ).trim();

  /* ==========================================================
     DOCUMENT URL
  ========================================================== */

  const getDocumentUrl = (documentPath) => {
    if (!documentPath) {
      return "";
    }

    const raw = String(documentPath).trim();

    if (/^https?:\/\//i.test(raw)) {
      return raw;
    }

    const normalized = raw.replace(/\\/g, "/");

    if (normalized.startsWith("/api/files/")) {
      return `${BASE_URL}${normalized}`;
    }

    if (normalized.startsWith("/uploads/")) {
      return `${BASE_URL}${normalized}`;
    }

    return `${BASE_URL}/api/files/${normalized.replace(/^\/+/, "")}`;
  };

  /* ==========================================================
     FETCH VENDORS
  ========================================================== */

  const fetchAllVendors = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/vendors`, getAuthConfig());

      setVendors(res.data || []);
    } catch (err) {
      logApiError("Error fetching vendors", err);
      handleAuthError(err);
    }
  };

  /* ==========================================================
     FETCH SELECTED VENDOR
  ========================================================== */

  const fetchVendor = async () => {
    try {
      if (isNewPO) {
        setVendor(null);
        return;
      }

      if (passedVendor) {
        setVendor(passedVendor);
        return;
      }

      const res = await axios.get(`${BASE_URL}/api/vendors`, getAuthConfig());

      const found = (res.data || []).find(
        (v) => String(v._id) === String(vendorId),
      );

      setVendor(found || null);
    } catch (err) {
      logApiError("Error fetching vendor", err);
      handleAuthError(err);
    }
  };

  /* ==========================================================
     FETCH QUOTE MASTER
  ========================================================== */

  const fetchQuoteMasterItems = async () => {
    const possibleUrls = [
      `${BASE_URL}/api/quote-master`,
      `${BASE_URL}/api/quotemaster`,
      `${BASE_URL}/api/quote-master-items`,
      `${BASE_URL}/api/master-items`,
      `${BASE_URL}/api/quoteMaster`,
    ];

    for (const url of possibleUrls) {
      try {
        const res = await axios.get(url, getAuthConfig());

        if (Array.isArray(res.data)) {
          return res.data;
        }

        if (Array.isArray(res.data?.items)) {
          return res.data.items;
        }

        if (Array.isArray(res.data?.data)) {
          return res.data.data;
        }
      } catch (err) {
        if (handleAuthError(err)) return [];
        console.warn("Quote master URL failed:", url);
      }
    }

    return [];
  };

  /* ==========================================================
     FETCH APPROVED QUOTE ITEMS
  ========================================================== */

  const fetchApprovedQuoteItems = async () => {
    try {
      const quoteRes = await axios.get(
        `${BASE_URL}/api/quotes?projectName=${encodeURIComponent(
          project.name,
        )}`,
        getAuthConfig(),
      );

      const approvedQuote = (quoteRes.data || [])
        .filter(
          (q) =>
            String(q.status || "")
              .trim()
              .toLowerCase() === "approved",
        )
        .sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0) -
            new Date(a.updatedAt || a.createdAt || 0),
        )[0];

      const quoteMasterItems = await fetchQuoteMasterItems();

      if (approvedQuote?.items?.length) {
        const items = approvedQuote.items.map((item, index) => {
          const isComplimentary = isComplimentaryQuoteItem(item);
          const itemName = item.label || item.name || item.itemName || "";

          const masterItem = quoteMasterItems.find(
            (m) =>
              normalizeText(m.label || m.name || m.itemName) ===
              normalizeText(itemName),
          );

          // Preserve recorded targets; otherwise use 50% of approved quote line value.
          const vendorTargetCost = positiveMoney(
            item.originalTargetCost,
            item.vendorTargetCost,
            item.vendorCost,
            item.venderCost,
            item.targetCost,
            masterItem?.originalTargetCost,
            masterItem?.vendorTargetCost,
            masterItem?.vendorCost,
            masterItem?.venderCost,
            masterItem?.targetCost,
            Math.round(approvedQuoteLineAmount(item) * 0.5 * 100) / 100,
          );

          return {
            id: index,

            itemName,

            so:
              approvedQuote.quoteNo ||
              approvedQuote.soNumber ||
              approvedQuote.salesOrderNo ||
              `SO-${String(approvedQuote._id || "").slice(-6)}`,

            qty: item.qty || item.quantity || 1,
            originalTargetCost: isComplimentary ? 0 : vendorTargetCost,
            vendorTargetCost: isComplimentary ? 0 : vendorTargetCost,
            isComplimentary,
            targetMissing: isComplimentary ? false : vendorTargetCost <= 0,
          };
        });

        setApprovedItems(items);
      } else {
        setApprovedItems([]);
      }
    } catch (err) {
      logApiError("Error fetching approved quote items", err);
      if (!handleAuthError(err)) setApprovedItems([]);
    }
  };

  // Needed in NEW mode as well, to discover other vendors using the same item.
  const fetchProjectPOs = async () => {
    try {
      const name = getCurrentProjectName();
      if (!name) return;
      const response = await axios.get(
        `${BASE_URL}/api/vendor-po?projectName=${encodeURIComponent(name)}`,
        getAuthConfig(),
      );
      setProjectPOs(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logApiError("Vendor target allocation fetch failed", error);
      handleAuthError(error);
    }
  };

  /* ==========================================================
     FETCH SAVED VENDOR PO
  ========================================================== */

  const fetchSavedVendorPO = async () => {
    try {
      if (!project?.name || isNewPO) {
        return;
      }

      const res = await axios.get(
        `${BASE_URL}/api/vendor-po?projectName=${encodeURIComponent(
          project.name,
        )}`,
        getAuthConfig(),
      );

      const list = Array.isArray(res.data)
        ? res.data
        : res.data
          ? [res.data]
          : [];

      setProjectPOs(list);

      const savedPO = list.find(
        (po) => String(po.vendorId) === String(vendorId),
      );

      if (savedPO?._id) {
        setSavedPOId(savedPO._id);
      }
      setGstEnabled(savedPO?.gstEnabled === true);
      setGstRate(
        savedPO?.gstEnabled === true ? String(savedPO.gstRate ?? "") : "",
      );
      setTdsEnabled(savedPO?.tdsEnabled === true);
      setTdsRate(
        savedPO?.tdsEnabled === true ? String(savedPO.tdsRate ?? "") : "",
      );

      /* --------------------------------------------
           LOAD SAVED DOCUMENT
        -------------------------------------------- */

      const loadedDocuments = Array.isArray(savedPO?.documents)
        ? savedPO.documents
        : savedPO?.document
          ? [
              {
                path: savedPO.document,
                name: savedPO.documentName || "Document",
                mimeType: savedPO.documentMimeType || "",
                uploadedAt: savedPO.documentUploadedAt || null,
              },
            ]
          : [];

      setSavedDocuments(loadedDocuments);

      if (savedPO?.items?.length) {
        setPoRows(
          savedPO.items.map((row) => {
            const vendorQty = row.vendorQty ?? "";

            const vendorRate = row.vendorRate ?? "";

            const vendorAmount =
              Number(vendorQty || 0) * Number(vendorRate || 0);

            const approved = approvedItems.find(
              (entry) =>
                normalizeText(entry.itemName) === normalizeText(row.itemName),
            );
            const complimentary =
              approved?.isComplimentary === true ||
              row.isComplimentary === true;
            return {
              ...row,
              isComplimentary: complimentary,
              targetMissing: complimentary ? false : row.targetMissing,
              originalTargetCost: complimentary ? 0 : row.originalTargetCost,
              targetCost: complimentary ? 0 : row.targetCost,
              vendorTargetCost: complimentary
                ? 0
                : positiveMoney(
                    row.vendorTargetCost,
                    row.targetCost,
                    approvedItems.find(
                      (entry) =>
                        normalizeText(entry.itemName) ===
                        normalizeText(row.itemName),
                    )?.vendorTargetCost,
                  ),

              vendorQty,

              vendorRate,

              vendorAmount,

              vendorId: row.vendorId || savedPO.vendorId || vendorId,

              vendorName: row.vendorName || savedPO.vendorName || "",

              vendorCode: row.vendorCode || savedPO.vendorCode || "",
            };
          }),
        );
      } else {
        setPoRows([]);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setPoRows([]);
        setSavedPOId(null);
        setGstEnabled(false);
        setGstRate("");
        setTdsEnabled(false);
        setTdsRate("");

        return;
      }

      logApiError("Error fetching saved Vendor PO", err);
      if (!handleAuthError(err)) setPoRows([]);
    }
  };

  /* ==========================================================
     ADD ITEM
  ========================================================== */

  // Clicking Add Item persists ONLY the item assignment immediately.
  // Vendor Qty and Rate remain editable and are finalized by Save Vendor PO.
  // This is required to update the target in vendor A's open quote as soon
  // as the same item is added to vendor B (rather than waiting for Save).
  const addItemToPO = async () => {
    if (itemSyncing) return;
    if (!vendor) return alert("Please select vendor first.");
    if (selectedItemIndex === "") return alert("Please select item.");
    const selectedItem = approvedItems[Number(selectedItemIndex)];
    if (!selectedItem) return;
    if (
      poRows.some(
        (row) =>
          normalizeText(row.itemName) === normalizeText(selectedItem.itemName),
      )
    ) {
      return alert("This item is already added for this vendor.");
    }
    const projectName = getCurrentProjectName();
    const resolvedVendorId = String(
      vendor._id || vendor.id || vendor.vendorId || "",
    );
    if (!projectName || !resolvedVendorId) {
      return alert(
        "Project or vendor is missing. Reopen Vendor PO from the project.",
      );
    }
    setItemSyncing(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/api/vendor-po/assign-item`,
        {
          projectName,
          vendorId: resolvedVendorId,
          vendorName: vendor.fullName || vendor.vendorName || vendor.name || "",
          vendorCode: vendor.vendorCode || vendor.code || "",
          item: {
            itemName: selectedItem.itemName,
            so: selectedItem.so,
            qty: selectedItem.qty,
            originalTargetCost: Number(
              selectedItem.originalTargetCost ??
                selectedItem.vendorTargetCost ??
                0,
            ),
            isComplimentary: selectedItem.isComplimentary === true,
            targetMissing:
              selectedItem.isComplimentary === true
                ? false
                : selectedItem.targetMissing,
            source: "MANUAL_VENDOR_PO",
          },
        },
        getAuthConfig(),
      );
      const serverPO = response.data?.data;
      // Never discard the user's as-yet-unsaved Qty and Rate in other rows.
      const previousRows = poRows;
      setPoRows((current) =>
        (serverPO?.items || []).map((row) => {
          const previous = current.find(
            (old) =>
              normalizeText(old.itemName) === normalizeText(row.itemName),
          );
          return previous
            ? {
                ...row,
                vendorQty: previous.vendorQty,
                vendorRate: previous.vendorRate,
                vendorAmount: previous.vendorAmount,
                originalTargetCost:
                  row.originalTargetCost ?? previous.originalTargetCost,
              }
            : row;
        }),
      );
      if (!serverPO?.items) setPoRows([...previousRows, selectedItem]);
      if (serverPO?._id) setSavedPOId(serverPO._id);
      if (Array.isArray(response.data?.projectPOs)) {
        setProjectPOs(response.data.projectPOs);
      } else {
        await fetchProjectPOs();
      }
      setSelectedItemIndex("");
      const assigned = (response.data?.projectPOs || []).flatMap((po) =>
        (po.items || []).filter(
          (row) =>
            normalizeText(row.itemName) ===
            normalizeText(selectedItem.itemName),
        ),
      );
      const first = assigned[0];
      const full = Number(
        first?.originalTargetCost ??
          selectedItem.originalTargetCost ??
          selectedItem.vendorTargetCost ??
          0,
      );
      const vendorCount = Number(first?.targetVendorCount || 1);
      if (vendorCount > 1) {
        setAllocationNotice(
          `${selectedItem.itemName}: Actual target ₹${full.toLocaleString("en-IN")}. ` +
            `This item is also added in another vendor's PO. ` +
            `It is divided between ${vendorCount} vendors; each vendor's target ` +
            `is shown below. Total original target remains ₹${full.toLocaleString("en-IN")}.`,
        );
      } else setAllocationNotice("");
    } catch (err) {
      logApiError("Failed to assign item", err);
      if (handleAuthError(err)) return;
      alert(
        err?.response?.data?.message ||
          "Could not assign item. Deploy the updated /api/vendor-po backend first.",
      );
    } finally {
      setItemSyncing(false);
    }
  };

  /* ==========================================================
     CHANGE ROW
  ========================================================== */

  const handleRowChange = (index, field, value) => {
    setPoRows((prevRows) =>
      prevRows.map((row, i) => {
        if (i !== index) {
          return row;
        }

        const updatedRow = {
          ...row,

          [field]: value,
        };

        const qty = Number(updatedRow.vendorQty || 0);

        const rate = Number(updatedRow.vendorRate || 0);

        updatedRow.vendorAmount = qty * rate;

        return updatedRow;
      }),
    );
  };

  /* ==========================================================
     REMOVE ROW
  ========================================================== */

  const removeRow = async (index) => {
    if (itemSyncing) return;
    const item = poRows[index];
    if (!item) return;
    const projectName = getCurrentProjectName();
    const resolvedVendorId = String(
      vendor?._id || vendor?.id || vendor?.vendorId || "",
    );
    if (!projectName || !resolvedVendorId) return;
    setItemSyncing(true);
    try {
      const response = await axios.post(
        `${BASE_URL}/api/vendor-po/unassign-item`,
        {
          projectName,
          vendorId: resolvedVendorId,
          itemName: item.itemName,
        },
        getAuthConfig(),
      );
      setPoRows((current) =>
        current.filter(
          (row) => normalizeText(row.itemName) !== normalizeText(item.itemName),
        ),
      );
      if (Array.isArray(response.data?.projectPOs)) {
        setProjectPOs(response.data.projectPOs);
      } else {
        await fetchProjectPOs();
      }
      setAllocationNotice("");
    } catch (err) {
      logApiError("Failed to remove assigned item", err);
      if (handleAuthError(err)) return;
      alert(err?.response?.data?.message || "Could not remove assigned item.");
    } finally {
      setItemSyncing(false);
    }
  };

  /* ==========================================================
     REAL-TIME TARGET SHARES (PREVIEW BEFORE SAVE)
     Server recalculates and persists the split on save.
  ========================================================== */
  const previewRows = useMemo(() => {
    const currentVendorId = String(
      vendor?._id || vendor?.id || vendor?.vendorId || "",
    );
    const currentProjectName = String(
      project?.name || project?.projectName || "",
    );
    if (!currentVendorId || !currentProjectName) return poRows;

    const otherPOs = projectPOs.filter(
      (po) => String(po.vendorId) !== currentVendorId,
    );
    const draft = {
      projectName: currentProjectName,
      vendorId: currentVendorId,
      items: poRows.map((item) => {
        const approved = approvedItems.find(
          (entry) =>
            String(entry.itemName || "")
              .trim()
              .toLowerCase() ===
            String(item.itemName || "")
              .trim()
              .toLowerCase(),
        );
        const otherOriginal = otherPOs
          .flatMap((po) => po.items || [])
          .find(
            (row) =>
              normalizeText(row.itemName) === normalizeText(item.itemName) &&
              positiveMoney(row.originalTargetCost) > 0,
          );
        const complimentary =
          approved?.isComplimentary === true || item.isComplimentary === true;
        return {
          ...item,
          isComplimentary: complimentary,
          targetMissing: complimentary ? false : item.targetMissing,
          originalTargetCost: complimentary
            ? 0
            : positiveMoney(
                item.originalTargetCost,
                approved?.originalTargetCost,
                otherOriginal?.originalTargetCost,
                approved?.vendorTargetCost,
                item.vendorTargetCost,
                item.targetCost,
              ),
          ...(complimentary ? { vendorTargetCost: 0, targetCost: 0 } : {}),
        };
      }),
    };
    const calculated = allocateProjectTargets([...otherPOs, draft]);

    const allocatedRows = calculated[calculated.length - 1]?.items || poRows;

    return allocatedRows.map((row) => {
      const approved = approvedItems.find(
        (item) =>
          normalizeText(item.itemName) === normalizeText(row.itemName) &&
          String(item.so || "") === String(row.so || ""),
      );

      const approvedQty = Number(approved?.qty);

      const complimentary =
        approved?.isComplimentary === true || row.isComplimentary === true;
      return {
        ...row,
        ...(complimentary
          ? {
              isComplimentary: true,
              targetMissing: false,
              originalTargetCost: 0,
              vendorTargetCost: 0,
              targetCost: 0,
              difference: -Number(row.vendorAmount || 0),
            }
          : {}),
        qty:
          Number.isFinite(approvedQty) && approvedQty > 0
            ? approvedQty
            : Number(row.qty ?? row.quantity ?? 1),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectPOs,
    poRows,
    vendor,
    approvedItems,
    project?.name,
    project?.projectName,
  ]);

  const missingTargetRows = previewRows.filter((row) => {
    if (row.isComplimentary === true) return false;

    if (row.targetMissing === false) return false;

    return !positiveMoney(
      row.originalTargetCost,
      row.targetCost,
      row.vendorTargetCost,
    );
  });

  const totalVendorAmount = previewRows.reduce(
    (sum, row) => sum + Number(row.vendorAmount || 0),
    0,
  );

  const totalTargetCost = previewRows.reduce(
    (sum, row) => sum + Number(row.targetCost ?? row.vendorTargetCost ?? 0),
    0,
  );

  const totalDifference = previewRows.reduce(
    (sum, row) =>
      sum +
      Number(row.targetCost ?? row.vendorTargetCost ?? 0) -
      Number(row.vendorAmount || 0),
    0,
  );

  const sharedTargetRows = previewRows.filter(
    (row) => Number(row.targetVendorCount || 1) > 1,
  );

  // Read the current vendor master stages; never use another vendor's stages.
  const currentVendorMaster = vendors.find(
    (entry) =>
      String(entry._id) ===
      String(vendor?._id || vendor?.id || vendor?.vendorId || ""),
  );
  const vendorPaymentStages = Array.isArray(currentVendorMaster?.paymentStages)
    ? currentVendorMaster.paymentStages
    : Array.isArray(vendor?.paymentStages)
      ? vendor.paymentStages
      : [];
  const paymentStageTotal =
    vendorPaymentStages.reduce(
      (sum, stage) => sum + Math.round(Number(stage.percentage || 0) * 100),
      0,
    ) / 100;
  const paymentStagesValid =
    vendorPaymentStages.length > 0 &&
    vendorPaymentStages.every(
      (stage) =>
        String(stage.stageName || "").trim() &&
        Number.isFinite(Number(stage.percentage)) &&
        Number(stage.percentage) > 0,
    ) &&
    Math.round(paymentStageTotal * 100) === 10000;

  const taxSummary = useMemo(
    () => calculateTaxes(previewRows, gstEnabled, gstRate, tdsEnabled, tdsRate),
    [previewRows, gstEnabled, gstRate, tdsEnabled, tdsRate],
  );

  /* ==========================================================
     DOCUMENT CHANGE
  ========================================================== */

  const handleDocumentChange = async (e) => {
    const input = e.target;
    const files = Array.from(input.files || []);

    if (!files.length) return;

    if (!vendor) {
      alert("Please select vendor first.");
      input.value = "";
      return;
    }

    const projectName = getCurrentProjectName();

    const resolvedVendorId = String(
      vendor?._id || vendor?.id || vendor?.vendorId || "",
    ).trim();

    const resolvedVendorName =
      vendor?.fullName || vendor?.vendorName || vendor?.name || "";

    const resolvedVendorCode = vendor?.vendorCode || vendor?.code || "";

    if (!projectName) {
      alert("Project name missing. Please reopen Vendor PO from the project.");
      input.value = "";
      return;
    }

    if (!resolvedVendorId) {
      alert("Vendor ID missing. Please select vendor again.");
      input.value = "";
      return;
    }

    const remainingSlots = 5 - savedDocuments.length;

    if (remainingSlots <= 0) {
      alert("Maximum 5 documents are allowed for one Vendor PO.");
      input.value = "";
      return;
    }

    if (files.length > remainingSlots) {
      alert(`You can upload only ${remainingSlots} more document(s).`);
      input.value = "";
      return;
    }

    const allowedExtensions = [
      ".pdf",
      ".jpg",
      ".jpeg",
      ".png",
      ".xlsx",
      ".xls",
      ".doc",
      ".docx",
    ];

    for (const file of files) {
      const fileName = String(file.name || "").toLowerCase();

      const validExtension = allowedExtensions.some((ext) =>
        fileName.endsWith(ext),
      );

      if (!validExtension) {
        alert(
          `${file.name}: Only PDF, JPG, PNG, Excel and Word documents are allowed.`,
        );
        input.value = "";
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name}: File size cannot be more than 10MB.`);
        input.value = "";
        return;
      }
    }

    const token = getAuthToken();

    if (!token) {
      setAuthError(
        "No login token available. Sign in again, then retry your upload.",
      );
      input.value = "";
      return;
    }

    try {
      setDocumentUploading(true);

      const formData = new FormData();

      files.forEach((file) => {
        formData.append("documents", file);
      });

      /*
       * IMPORTANT:
       * Project/vendor details are sent in the URL query string.
       * This avoids depending on multipart text-field parsing.
       */
      const uploadUrl =
        `${BASE_URL}/api/vendor-po/documents` +
        `?projectName=${encodeURIComponent(projectName)}` +
        `&vendorId=${encodeURIComponent(resolvedVendorId)}` +
        `&vendorName=${encodeURIComponent(resolvedVendorName)}` +
        `&vendorCode=${encodeURIComponent(resolvedVendorCode)}`;

      console.log("Vendor PO upload:", {
        projectName,
        vendorId: resolvedVendorId,
        vendorName: resolvedVendorName,
        vendorCode: resolvedVendorCode,
        files: files.map((file) => file.name),
      });

      const res = await axios.post(uploadUrl, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const docs = Array.isArray(res?.data?.documents)
        ? res.data.documents
        : [];

      setSavedDocuments(docs);
      setAuthError("");

      if (res?.data?.data?._id) {
        setSavedPOId(res.data.data._id);
      }

      alert("✅ Document uploaded and saved successfully.");
    } catch (err) {
      logApiError("Vendor PO document upload error", err);
      if (handleAuthError(err)) return;

      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to upload document.",
      );
    } finally {
      setDocumentUploading(false);
      input.value = "";
    }
  };

  const removeSavedDocument = async (doc) => {
    if (!doc?._id || !savedPOId) {
      alert("This document cannot be removed from here.");
      return;
    }

    const ok = window.confirm(`Remove "${doc.name || "document"}"?`);

    if (!ok) return;

    try {
      const res = await axios.delete(
        `${BASE_URL}/api/vendor-po/${savedPOId}/documents/${doc._id}`,
        getAuthConfig(),
      );

      setSavedDocuments(
        Array.isArray(res?.data?.documents) ? res.data.documents : [],
      );
    } catch (err) {
      logApiError("Vendor PO document delete error", err);
      if (handleAuthError(err)) return;

      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to remove document.",
      );
    }
  };

  /* ==========================================================
     SAVE VENDOR PO
  ========================================================== */

  const saveVendorPO = async ({
    navigateAfterSave = true,
    showSuccess = true,
  } = {}) => {
    if (itemSyncing) return;
    const token = getAuthToken();

    if (!token) {
      setAuthError(
        "No login token available. Sign in again, then retry saving. Your PO items are preserved on this page.",
      );
      return;
    }

    if (!vendor) {
      alert("Please select vendor first.");
      return;
    }

    if (!poRows.length) {
      alert("Please add at least one item.");
      return;
    }
    // Vendor Qty and Vendor Rate are mandatory for every item.
    const invalidItem = poRows.find((row) => {
      const vendorQty = Number(row.vendorQty);
      const vendorRate = Number(row.vendorRate);

      return (
        String(row.vendorQty ?? "").trim() === "" ||
        String(row.vendorRate ?? "").trim() === "" ||
        !Number.isFinite(vendorQty) ||
        !Number.isFinite(vendorRate) ||
        vendorQty <= 0 ||
        vendorRate <= 0
      );
    });

    if (invalidItem) {
      alert(
        `Please enter Vendor Qty and Vendor Rate greater than zero for "${invalidItem.itemName || "item"}" before saving.`,
      );
      return;
    }

    const projectName = getCurrentProjectName();

    const resolvedVendorId = String(
      vendor?._id || vendor?.id || vendor?.vendorId || "",
    ).trim();

    const resolvedVendorName =
      vendor?.fullName || vendor?.vendorName || vendor?.name || "";

    const resolvedVendorCode = vendor?.vendorCode || vendor?.code || "";

    if (!projectName) {
      alert("Project name missing. Please reopen Vendor PO from the project.");
      return;
    }

    if (!resolvedVendorId) {
      alert("Vendor ID missing. Please select vendor again.");
      return;
    }

    const invalidRate = (enabled, value) =>
      enabled &&
      (String(value).trim() === "" ||
        !Number.isFinite(Number(value)) ||
        Number(value) <= 0 ||
        Number(value) > 100);
    if (invalidRate(gstEnabled, gstRate)) {
      alert("Please enter a valid GST rate (greater than 0 and up to 100%).");
      return;
    }
    if (invalidRate(tdsEnabled, tdsRate)) {
      alert("Please enter a valid TDS rate (greater than 0 and up to 100%).");
      return;
    }

    try {
      const fixedItems = previewRows.map((row) => ({
        ...row,
        originalTargetCost: Number(
          row.originalTargetCost ?? row.vendorTargetCost ?? 0,
        ),
        targetCost: Number(row.targetCost ?? row.vendorTargetCost ?? 0),
        vendorTargetCost: Number(row.vendorTargetCost ?? row.targetCost ?? 0),
        vendorAmount: Number(row.vendorAmount || 0),
        vendorId: resolvedVendorId,
        vendorName: resolvedVendorName,
        vendorCode: resolvedVendorCode,
      }));

      /*
       * No file is uploaded in /save, so send normal JSON.
       * Express can parse this directly with express.json().
       */
      const payload = {
        projectName,
        vendorId: resolvedVendorId,
        vendorName: resolvedVendorName,
        vendorCode: resolvedVendorCode,
        items: fixedItems,
        // The backend recalculates amounts from the item rows, not these totals.
        baseAmount: taxSummary.baseAmount,
        gstEnabled,
        gstRate: gstEnabled ? Number(gstRate) : 0,
        gstAmount: taxSummary.gstAmount,
        tdsEnabled,
        tdsRate: tdsEnabled ? Number(tdsRate) : 0,
        tdsAmount: taxSummary.tdsAmount,
        totalAmount: taxSummary.netPayable,
      };

      const res = await axios.post(`${BASE_URL}/api/vendor-po/save`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const saved = res?.data?.data;
      // Avoid reporting a successful tax save against an old /save route that
      // silently ignores GST/TDS fields and returns a base-only PO.
      if (
        !saved ||
        typeof saved.gstEnabled !== "boolean" ||
        typeof saved.tdsEnabled !== "boolean" ||
        saved.gstEnabled !== gstEnabled ||
        saved.tdsEnabled !== tdsEnabled ||
        (gstEnabled && Number(saved.gstRate) !== Number(gstRate)) ||
        (tdsEnabled && Number(saved.tdsRate) !== Number(tdsRate))
      ) {
        alert(
          "PO tax settings were not confirmed by the server. Update the vendor-po backend before continuing.",
        );
        return;
      }
      if (saved._id) setSavedPOId(saved._id);
      if (Array.isArray(res?.data?.documents)) {
        setSavedDocuments(res.data.documents);
      }

      setAuthError("");
      if (Array.isArray(res?.data?.projectPOs))
        setProjectPOs(res.data.projectPOs);
      if (showSuccess) alert("✅ Vendor PO saved successfully.");

      setPoRows(Array.isArray(saved.items) ? saved.items : fixedItems);

      if (navigateAfterSave) {
        navigate("/vendor-po", { state: { project } });
      }
      return saved._id;
    } catch (err) {
      logApiError("Error saving Vendor PO", err);

      if (handleAuthError(err)) return;

      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to save Vendor PO.",
      );
    }
  };

  /* ==========================================================
     ONE-CLICK: SAVE CURRENT PO, GENERATE PDF ON BACKEND, EMAIL VIA BREVO.
     Vendor email is always read fresh from Vendor Master on the server.
     Never send frontend target costs / backend API credentials to vendor.
  ========================================================== */
  /* ==========================================================
     DOWNLOAD VENDOR PO PDF — save latest values first.
  ========================================================== */
  const downloadVendorPoPdf = async () => {
    if (downloadingPoPdf || sendingQuotePdf || itemSyncing) return;
    setDownloadingPoPdf(true);
    try {
      const id = await saveVendorPO({
        navigateAfterSave: false,
        showSuccess: false,
      });
      if (!id) return;

      const response = await axios.get(
        `${BASE_URL}/api/vendor-po/${encodeURIComponent(id)}/download-pdf`,
        { ...getAuthConfig(), responseType: "blob" },
      );
      if (!response.data?.size) throw new Error("PDF file is empty.");
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Vendor-PO-${id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      logApiError("Vendor PO PDF download failed", err);
      if (handleAuthError(err)) return;
      let message = err?.message || "Unable to download Vendor PO PDF.";
      if (err?.response?.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          message = JSON.parse(errorText)?.message || message;
        } catch (_) {
          // Keep the original error when the response is not JSON.
        }
      } else {
        message = err?.response?.data?.message || message;
      }
      alert(message);
    } finally {
      setDownloadingPoPdf(false);
    }
  };

  const sendVendorQuotePdf = async () => {
    if (sendingQuotePdf || downloadingPoPdf || itemSyncing) return;
    const missing = previewRows.find(
      (row) =>
        !(Number(row.vendorQty ?? row.qty ?? 0) > 0) ||
        !(Number(row.vendorAmount) > 0),
    );
    if (missing) {
      alert(
        `Enter a valid Vendor Qty and Vendor Rate for "${missing.itemName || "item"}" before sending.`,
      );
      return;
    }

    setSendingQuotePdf(true);
    try {
      // Save without navigating so a single click uses the CURRENT edited values.
      // No email is sent if saving or tax persistence validation failed.
      const id = await saveVendorPO({
        navigateAfterSave: false,
        showSuccess: false,
      });
      if (!id) return;
      const response = await axios.post(
        `${BASE_URL}/api/vendor-po/${encodeURIComponent(id)}/send-quote-pdf`,
        {},
        getAuthConfig(),
      );
      if (response.data?.success !== true)
        throw new Error("Backend did not confirm mail acceptance.");
      alert(
        `Your vendor quote PDF is on its way to ${response.data.sentTo}. Please allow a few minutes for delivery.`,
      );
    } catch (err) {
      logApiError("Vendor quote PDF email failed", err);
      if (handleAuthError(err)) return;
      alert(
        [err?.response?.data?.message, err?.response?.data?.brevoMessage]
          .filter(Boolean)
          .join("\n") ||
          err?.message ||
          "Could not send Vendor Quote PDF.",
      );
    } finally {
      setSendingQuotePdf(false);
    }
  };

  /* ==========================================================
     UI
  ========================================================== */

  return (
    <div className="vd-page" style={styles.page}>
      {authError && (
        <div className="vd-auth" style={styles.authNotice} role="alert">
          <div style={{ flex: 1 }}>
            <strong style={{ display: "block", marginBottom: 5 }}>
              Authentication required
            </strong>
            <span>{authError}</span>
          </div>
          <button
            type="button"
            style={styles.authRetryBtn}
            onClick={retryAfterLogin}
          >
            Retry after sign-in
          </button>
        </div>
      )}
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="vd-header" style={styles.header}>
        <button
          className="vd-back"
          onClick={() =>
            navigate("/vendor-po", {
              state: {
                project,
              },
            })
          }
          style={styles.backBtn}
        >
          ← Back
        </button>

        <div className="vd-header-center" style={styles.headerCenter}>
          <h2 className="vd-title" style={styles.title}>
            Vendor PO - {vendor?.fullName || "New Vendor"}
          </h2>

          <p style={styles.projectText}>Project: {project?.name || "-"}</p>
        </div>

        <div className="vd-header-right" style={styles.headerRight}>
          <div style={styles.vendorBadge}>
            {vendor?.vendorCode || "Vendor Code"}
          </div>
        </div>
      </div>

      {/* ======================================================
          PROJECT ERROR
      ====================================================== */}

      {!project?.name && (
        <div style={styles.errorBox}>
          Project not found. Please open this page from project.
        </div>
      )}

      {/* ======================================================
          ADD PO ITEMS
      ====================================================== */}

      <div className="vd-control-card" style={styles.controlCard}>
        <div className="vd-control-header" style={styles.controlHeader}>
          <div>
            <div style={styles.sectionEyebrow}>PURCHASE ORDER</div>

            <h3 style={styles.sectionTitle}>Add PO Items</h3>
          </div>

          <div className="vd-summary-mini" style={styles.summaryMini}>
            <span style={styles.summaryMiniLabel}>Current Total</span>

            <strong style={styles.summaryMiniValue}>
              ₹{" "}
              {totalVendorAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,

                maximumFractionDigits: 2,
              })}
            </strong>
          </div>
        </div>

        <div className="vd-controls-row" style={styles.controlsRow}>
          {/* ================================================
              VENDOR
          ================================================ */}

          {isNewPO && (
            <div className="vd-field" style={styles.fieldGroup}>
              <label style={styles.label}>Select Vendor</label>

              <select
                value={vendor?._id || ""}
                onChange={(e) => {
                  const selectedVendor = vendors.find(
                    (v) => String(v._id) === String(e.target.value),
                  );

                  setVendor(selectedVendor || null);

                  setPoRows([]);

                  setSavedPOId(null);

                  setSavedDocuments([]);
                  setGstEnabled(false);
                  setGstRate("");
                  setTdsEnabled(false);
                  setTdsRate("");
                }}
                style={styles.select}
              >
                <option value="">Select Vendor</option>

                {vendors.map((v) => (
                  <option key={v._id} value={v._id}>
                    {v.vendorCode || "NA"} - {v.fullName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ================================================
              APPROVED ITEM
          ================================================ */}

          <div className="vd-field-wide" style={styles.fieldGroupWide}>
            <label style={styles.label}>Select Approved Item</label>

            <select
              value={selectedItemIndex}
              onChange={(e) => setSelectedItemIndex(e.target.value)}
              style={styles.select}
            >
              <option value="">Select Item</option>

              {approvedItems.map((item, index) => (
                <option key={index} value={index} title={item.itemName}>
                  {shortText(item.itemName)}
                </option>
              ))}
            </select>
          </div>

          {/* ================================================
              ADD ITEM
          ================================================ */}

          <button
            className="vd-add"
            onClick={addItemToPO}
            disabled={itemSyncing}
            style={{ ...styles.addBtn, opacity: itemSyncing ? 0.6 : 1 }}
          >
            {itemSyncing ? "Syncing item..." : "+ Add Item"}
          </button>

          {/* ================================================
              UPLOAD DOCUMENTS - AUTO SAVE, MAX 5
          ================================================ */}

          <label
            className="vd-upload"
            style={{
              ...styles.uploadBtn,
              opacity:
                documentUploading || savedDocuments.length >= 5 ? 0.65 : 1,
              cursor:
                documentUploading || savedDocuments.length >= 5
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {documentUploading
              ? "Uploading..."
              : `Upload Document (${savedDocuments.length}/5)`}

            <input
              type="file"
              multiple
              disabled={documentUploading || savedDocuments.length >= 5}
              accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
              style={{ display: "none" }}
              onChange={handleDocumentChange}
            />
          </label>

          {savedDocuments.length > 0 && (
            <div className="vd-docs" style={styles.savedDocumentsList}>
              {savedDocuments.map((doc, index) => (
                <div
                  key={doc?._id || `${doc?.path || "document"}-${index}`}
                  className="vd-doc-item"
                  style={styles.savedDocumentItem}
                >
                  <a
                    href={getDocumentUrl(doc?.path || doc?.document || "")}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.savedDocumentLink}
                    title={doc?.name || `Document ${index + 1}`}
                  >
                    📎 {shortText(doc?.name || `Document ${index + 1}`, 28)}
                  </a>

                  {doc?._id && (
                    <button
                      type="button"
                      style={styles.clearDocumentBtn}
                      onClick={() => removeSavedDocument(doc)}
                      title="Remove saved document"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ======================================================
          PO TABLE
      ====================================================== */}

      <div className="vd-table-card" style={styles.tableCard}>
        <div className="vd-table-header" style={styles.tableHeader}>
          <div>
            <div style={styles.sectionEyebrow}>PO DETAILS</div>

            <h3 style={styles.sectionTitle}>Vendor Purchase Order</h3>
          </div>

          <div style={styles.itemCountBadge}>{poRows.length} Items</div>
        </div>

        {allocationNotice && (
          <div
            role="status"
            style={{
              margin: "10px 16px",
              padding: "10px 14px",
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
              borderRadius: 9,
              color: "#1e40af",
              fontSize: 13,
            }}
          >
            {allocationNotice}
          </div>
        )}

        {missingTargetRows.length > 0 && (
          <div
            role="alert"
            style={{
              margin: "10px 16px",
              padding: "10px 14px",
              border: "1px solid #fca5a5",
              background: "#fff7ed",
              borderRadius: 9,
              color: "#9a3412",
              fontSize: 13,
            }}
          >
            Target Cost not configured for:{" "}
            {missingTargetRows.map((r) => r.itemName).join(", ")}. Enter the
            original target in Vendor Quote or Approved Quote and refresh; a 50%
            of the approved quote line amount could not be calculated.
          </div>
        )}

        {sharedTargetRows.length > 0 && (
          <div
            style={{
              margin: "10px 16px",
              padding: "12px 14px",
              border: "1px solid #facc15",
              background: "#fffbeb",
              borderRadius: 9,
              color: "#854d0e",
              fontSize: 13,
            }}
            role="status"
          >
            <strong>
              Shared item target cost — original budget is counted only once.
            </strong>
            {sharedTargetRows.map((item, idx) => (
              <div key={`${item.itemName}-${idx}`} style={{ marginTop: 5 }}>
                {item.itemName}: Actual target ₹
                {Number(item.originalTargetCost || 0).toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
                . This item is also added in another vendor's PO. Divided
                between {item.targetVendorCount} vendors; current vendor target
                ₹
                {Number(item.targetCost || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                .
              </div>
            ))}
          </div>
        )}
        <div className="vd-table-wrap" style={styles.tableWrap}>
          <table className="vd-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.thCenter}>Sr No</th>

                <th
                  style={{ ...styles.th, width: "360px", whiteSpace: "normal" }}
                >
                  Item
                </th>

                <th style={styles.thCenter}>SO</th>

                <th style={styles.thCenter}>Qty</th>

                <th style={styles.thRight}>Target Cost</th>

                <th style={styles.thCenter}>Vendor Qty</th>

                <th style={styles.thRight}>Vendor Rate</th>
                <th style={styles.thRight}>Vendor Amount</th>

                <th style={styles.thRight}>Difference</th>

                <th style={styles.thCenter}>Action</th>
              </tr>
            </thead>

            <tbody>
              {poRows.length === 0 ? (
                <tr className="vd-row">
                  <td colSpan="10" style={styles.emptyCell}>
                    No PO items added for this vendor.
                  </td>
                </tr>
              ) : (
                previewRows.map((row, index) => {
                  const difference =
                    Number(row.targetCost ?? row.vendorTargetCost ?? 0) -
                    Number(row.vendorAmount || 0);

                  return (
                    <tr key={index} className="vd-row">
                      <td className="vd-c-sr" style={styles.tdCenter}>
                        {index + 1}
                      </td>

                      <td
                        className="vd-c-item"
                        style={{ ...styles.td, verticalAlign: "top" }}
                      >
                        <div
                          style={styles.itemNameWrap}
                          title={row.itemName || ""}
                        >
                          {row.itemName || "-"}
                          {row.quoteSyncReview && (
                            <div style={{ color: "#b45309", marginTop: 5 }}>
                              Removed from current approved quote — priced PO
                              item retained for review.
                            </div>
                          )}
                        </div>
                      </td>

                      <td data-label="SO" style={styles.tdCenter}>
                        <input
                          style={styles.readOnlyInputCenter}
                          value={row.so || ""}
                          readOnly
                        />
                      </td>

                      <td data-label="Qty" style={styles.tdCenter}>
                        <input
                          style={styles.readOnlyInputCenter}
                          value={row.qty ?? row.quantity ?? ""}
                          readOnly
                        />
                      </td>

                      <td data-label="Target Cost" style={styles.tdRight}>
                        <input
                          style={styles.readOnlyInputRight}
                          value={
                            row.isComplimentary === true
                              ? "0.00"
                              : positiveMoney(
                                    row.targetCost,
                                    row.vendorTargetCost,
                                  ) > 0
                                ? positiveMoney(
                                    row.targetCost,
                                    row.vendorTargetCost,
                                  ).toFixed(2)
                                : "Target not set"
                          }
                          readOnly
                        />
                      </td>

                      <td data-label="Vendor Qty" style={styles.tdCenter}>
                        <input
                          type="number"
                          inputMode="decimal"
                          style={styles.editInputCenter}
                          value={row.vendorQty || ""}
                          onChange={(e) =>
                            handleRowChange(index, "vendorQty", e.target.value)
                          }
                        />
                      </td>

                      <td data-label="Vendor Rate" style={styles.tdRight}>
                        <input
                          type="number"
                          inputMode="decimal"
                          style={styles.editInputRight}
                          value={row.vendorRate || ""}
                          onChange={(e) =>
                            handleRowChange(index, "vendorRate", e.target.value)
                          }
                        />
                      </td>
                      <td data-label="Vendor Amount" style={styles.tdRight}>
                        <div style={styles.amountText}>
                          ₹ {Number(row.vendorAmount || 0).toFixed(2)}
                        </div>
                      </td>

                      <td data-label="Difference" style={styles.tdRight}>
                        <div
                          className="vd-diff"
                          style={{
                            ...styles.diffBadge,

                            ...(difference < 0
                              ? styles.diffNegative
                              : styles.diffPositive),
                          }}
                        >
                          {formatDifferenceWithPercent(
                            difference,
                            row.targetCost ?? row.vendorTargetCost ?? 0,
                          )}
                        </div>
                      </td>

                      <td className="vd-c-action" style={styles.tdCenter}>
                        <button
                          style={styles.removeBtn}
                          onClick={() => removeRow(index)}
                          disabled={itemSyncing}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {poRows.length > 0 && (
              <tfoot>
                <tr className="vd-total-row" style={styles.totalRow}>
                  <td colSpan="4" style={styles.totalLabelCell}>
                    TOTAL
                  </td>

                  <td data-label="Target Cost" style={styles.totalValueCell}>
                    ₹ {totalTargetCost.toFixed(2)}
                  </td>

                  <td></td>

                  <td></td>

                  <td data-label="Vendor Amount" style={styles.totalValueCell}>
                    ₹ {totalVendorAmount.toFixed(2)}
                  </td>

                  <td data-label="Difference" style={styles.totalValueCell}>
                    {formatDifferenceWithPercent(
                      totalDifference,
                      totalTargetCost,
                    )}
                  </td>

                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* GST and TDS are disabled by default for each Vendor PO. */}
      {poRows.length > 0 && (
        <section
          className="vd-tax"
          style={styles.taxCard}
          aria-label="PO GST and TDS"
        >
          <div style={styles.taxTitle}>GST &amp; TDS</div>
          <div style={styles.taxHint}>
            Tick a checkbox only when that tax is applicable to this vendor PO.
            Enter the applicable rate manually. Both calculations use the base
            Vendor Amount (excluding GST).
          </div>
          <div className="vd-tax-row" style={styles.taxControlRow}>
            <div className="vd-tax-control" style={styles.taxControl}>
              <label style={styles.taxCheckLabel}>
                <input
                  type="checkbox"
                  checked={gstEnabled}
                  onChange={(e) => setGstEnabled(e.target.checked)}
                  disabled={itemSyncing}
                />
                Apply GST
              </label>
              {gstEnabled && (
                <label style={styles.taxRateLabel}>
                  GST %
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0.01"
                    max="100"
                    step="0.01"
                    placeholder="Enter GST %"
                    value={gstRate}
                    onChange={(e) => setGstRate(e.target.value)}
                    disabled={itemSyncing}
                    style={styles.taxRateInput}
                    aria-label="GST percentage"
                  />
                </label>
              )}
            </div>
            <div className="vd-tax-control" style={styles.taxControl}>
              <label style={styles.taxCheckLabel}>
                <input
                  type="checkbox"
                  checked={tdsEnabled}
                  onChange={(e) => setTdsEnabled(e.target.checked)}
                  disabled={itemSyncing}
                />
                Apply TDS
              </label>
              {tdsEnabled && (
                <label style={styles.taxRateLabel}>
                  TDS %
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0.01"
                    max="100"
                    step="0.01"
                    placeholder="Enter TDS %"
                    value={tdsRate}
                    onChange={(e) => setTdsRate(e.target.value)}
                    disabled={itemSyncing}
                    style={styles.taxRateInput}
                    aria-label="TDS percentage"
                  />
                </label>
              )}
            </div>
            <div className="vd-tax-totals" style={styles.taxTotals}>
              <div style={styles.taxMoneyRow}>
                <span>Base Vendor Amount</span>
                <strong>₹ {formatMoney(taxSummary.baseAmount)}</strong>
              </div>
              {gstEnabled && (
                <div style={styles.taxMoneyRow}>
                  <span>GST (+{gstRate || "0"}%)</span>
                  <strong>+ ₹ {formatMoney(taxSummary.gstAmount)}</strong>
                </div>
              )}
              {tdsEnabled && (
                <div style={styles.taxMoneyRow}>
                  <span>TDS (−{tdsRate || "0"}%)</span>
                  <strong>− ₹ {formatMoney(taxSummary.tdsAmount)}</strong>
                </div>
              )}
              <div style={{ ...styles.taxMoneyRow, ...styles.taxGrandTotal }}>
                <span>Net Payable</span>
                <strong>₹ {formatMoney(taxSummary.netPayable)}</strong>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Vendor-wise stages from Vendor Master; amounts use current Net Payable. */}
      {vendor && (
        <section
          className="vd-stages"
          style={{
            margin: "12px 0",
            padding: 14,
            background: "#fff",
            border: "1px solid #dce4f0",
            borderRadius: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 10,
            }}
          >
            <h3 style={{ margin: 0, fontSize: 16, color: "#343a76" }}>
              Vendor Payment Stages
            </h3>
            <strong
              style={{
                color: paymentStagesValid ? "#15803d" : "#b91c1c",
                fontSize: 13,
              }}
            >
              Total: {paymentStageTotal.toFixed(2)}%{" "}
              {paymentStagesValid ? "✓" : "— Check Vendor Master"}
            </strong>
          </div>
          {vendorPaymentStages.length ? (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#343a76",
                      color: "#fff",
                      textAlign: "left",
                    }}
                  >
                    <th style={{ padding: 9 }}>Sr.</th>
                    <th style={{ padding: 9 }}>Stage Name</th>
                    <th style={{ padding: 9, textAlign: "right" }}>
                      Payment %
                    </th>
                    <th style={{ padding: 9, textAlign: "right" }}>
                      Amount (₹)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vendorPaymentStages.map((stage, index) => {
                    const netCents = Math.round(taxSummary.netPayable * 100);
                    const previousCents = vendorPaymentStages
                      .slice(0, index)
                      .reduce(
                        (sum, row) =>
                          sum +
                          Math.round(
                            (netCents * Number(row.percentage || 0)) / 100,
                          ),
                        0,
                      );
                    const amountCents =
                      index === vendorPaymentStages.length - 1 &&
                      paymentStagesValid
                        ? netCents - previousCents
                        : Math.round(
                            (netCents * Number(stage.percentage || 0)) / 100,
                          );
                    return (
                      <tr
                        key={`${index}-${stage.stageName}`}
                        style={{ borderBottom: "1px solid #e8edf5" }}
                      >
                        <td style={{ padding: 9 }}>{index + 1}</td>
                        <td style={{ padding: 9 }}>{stage.stageName}</td>
                        <td style={{ padding: 9, textAlign: "right" }}>
                          {Number(stage.percentage || 0).toFixed(2)}%
                        </td>
                        <td style={{ padding: 9, textAlign: "right" }}>
                          ₹ {formatMoney(amountCents / 100)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700, background: "#f6f8fc" }}>
                    <td colSpan={2} style={{ padding: 9 }}>
                      Total / Net Payable
                    </td>
                    <td style={{ padding: 9, textAlign: "right" }}>
                      {paymentStageTotal.toFixed(2)}%
                    </td>
                    <td style={{ padding: 9, textAlign: "right" }}>
                      ₹ {formatMoney(taxSummary.netPayable)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div style={{ color: "#b91c1c", fontSize: 13 }}>
              No payment stages found for this vendor. Define stages totaling
              100% in Vendor Master.
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: "#64748b" }}>
            Preview based on Vendor Master and current PO Net Payable. This
            display does not save stages into the PO or its PDF.
          </div>
        </section>
      )}

      {/* ======================================================
          BOTTOM BAR
      ====================================================== */}

      {poRows.length > 0 && (
        <div className="vd-bottom" style={styles.bottomBar}>
          <div style={styles.bottomTotal}>
            <span style={styles.bottomTotalLabel}>Net Payable</span>

            <strong style={styles.bottomTotalValue}>
              ₹ {formatMoney(taxSummary.netPayable)}
            </strong>
          </div>

          <div
            className="vd-bottom-actions"
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 9,
            }}
          >
            <button
              type="button"
              style={styles.saveBtn}
              onClick={() => saveVendorPO()}
              disabled={itemSyncing || sendingQuotePdf || downloadingPoPdf}
            >
              {savedPOId ? "Update Vendor PO" : "Save Vendor PO"}
            </button>
            <button
              type="button"
              style={{
                ...styles.saveBtn,
                background: "#343a76",
                opacity:
                  itemSyncing || sendingQuotePdf || downloadingPoPdf ? 0.65 : 1,
              }}
              onClick={downloadVendorPoPdf}
              disabled={itemSyncing || sendingQuotePdf || downloadingPoPdf}
              title="Save current PO and download PDF"
            >
              {downloadingPoPdf ? "Downloading PDF..." : "Download PDF"}
            </button>
            <button
              type="button"
              style={{
                ...styles.saveBtn,
                background: "#15803d",
                opacity: itemSyncing || sendingQuotePdf ? 0.65 : 1,
              }}
              onClick={sendVendorQuotePdf}
              disabled={itemSyncing || sendingQuotePdf || downloadingPoPdf}
              title="Save current PO, create vendor-facing PDF, and email the Vendor Master address"
            >
              {sendingQuotePdf
                ? "Saving & Sending PDF..."
                : "Send Quote PDF to Vendor"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================
   STYLES
============================================================ */

const styles = {
  taxCard: {
    marginTop: 14,
    padding: "18px 20px",
    background: "#fff",
    border: "1px solid #dbe3ec",
    borderRadius: 12,
    boxShadow: "0 5px 16px rgba(15,23,42,0.05)",
  },
  taxTitle: { fontSize: 17, fontWeight: 900, color: "#0f172a" },
  taxHint: { marginTop: 5, color: "#64748b", fontSize: 12, lineHeight: 1.5 },
  taxControlRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 14,
    alignItems: "stretch",
    marginTop: 14,
  },
  taxControl: {
    flex: "1 1 185px",
    minWidth: 185,
    borderRadius: 9,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    padding: 13,
  },
  taxCheckLabel: {
    display: "flex",
    gap: 9,
    alignItems: "center",
    cursor: "pointer",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 800,
  },
  taxRateLabel: {
    display: "flex",
    gap: 6,
    flexDirection: "column",
    marginTop: 13,
    color: "#475569",
    fontSize: 12,
    fontWeight: 800,
  },
  taxRateInput: {
    maxWidth: 180,
    padding: "9px 10px",
    fontSize: 13,
    background: "white",
    border: "1px solid #94a3b8",
    borderRadius: 7,
    boxSizing: "border-box",
  },
  taxTotals: {
    flex: "1 1 310px",
    minWidth: 270,
    display: "flex",
    flexDirection: "column",
    gap: 9,
    borderRadius: 9,
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    padding: 13,
    color: "#166534",
    fontSize: 13,
  },
  taxMoneyRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
  },
  taxGrandTotal: {
    borderTop: "1px solid #86efac",
    paddingTop: 9,
    fontWeight: 900,
    fontSize: 15,
  },
  authNotice: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    padding: "12px 16px",
    marginBottom: "14px",
    borderRadius: "10px",
    border: "1px solid #fca5a5",
    background: "#fff7ed",
    color: "#991b1b",
    fontSize: "13px",
  },
  authRetryBtn: {
    background: "#1d4ed8",
    color: "#ffffff",
    border: 0,
    borderRadius: "7px",
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 700,
  },
  page: {
    minHeight: "100vh",

    padding: "34px",

    background: "linear-gradient(135deg, #f5f7fb 0%, #eef4ff 100%)",

    fontFamily: "Calibri, Arial, sans-serif",
  },

  header: {
    background: "#fff",

    borderRadius: "16px",

    padding: "18px 22px",

    marginBottom: "22px",

    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",

    display: "grid",

    gridTemplateColumns: "120px 1fr 220px",

    alignItems: "center",

    gap: "15px",

    border: "1px solid #e5e7eb",
  },

  backBtn: {
    background: "#2563eb",

    color: "#fff",

    border: "none",

    padding: "10px 18px",

    borderRadius: "9px",

    cursor: "pointer",

    fontWeight: 800,

    fontSize: "14px",
  },

  headerCenter: {
    textAlign: "center",
  },

  headerRight: {
    justifySelf: "end",
  },

  title: {
    margin: 0,

    fontSize: "26px",

    fontWeight: 900,

    color: "#0f172a",
  },

  projectText: {
    margin: "6px 0 0",

    color: "#64748b",

    fontWeight: 700,

    overflowWrap: "anywhere",
  },

  vendorBadge: {
    background: "#eff6ff",

    color: "#1d4ed8",

    border: "1px solid #bfdbfe",

    padding: "9px 14px",

    borderRadius: "999px",

    fontWeight: 900,

    whiteSpace: "nowrap",
  },

  errorBox: {
    padding: "14px",

    background: "#fee2e2",

    color: "#991b1b",

    border: "1px solid #fecaca",

    borderRadius: "10px",

    fontWeight: 800,

    marginBottom: "18px",
  },

  controlCard: {
    background: "#fff",

    border: "1px solid #e5e7eb",

    borderRadius: "12px",

    padding: "12px 18px",

    marginBottom: "14px",

    boxShadow: "0 5px 16px rgba(15, 23, 42, 0.05)",
  },

  controlHeader: {
    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    gap: "16px",

    marginBottom: "10px",
  },

  sectionEyebrow: {
    color: "#2563eb",

    fontSize: "10px",

    fontWeight: 900,

    letterSpacing: "1.2px",

    marginBottom: "3px",
  },

  sectionTitle: {
    margin: 0,

    fontSize: "18px",

    lineHeight: 1.2,

    fontWeight: 900,

    color: "#0f172a",
  },

  summaryMini: {
    minWidth: "190px",

    background: "#f0fdf4",

    border: "1px solid #bbf7d0",

    borderRadius: "12px",

    padding: "10px 14px",

    display: "flex",

    flexDirection: "column",

    alignItems: "flex-end",
  },

  summaryMiniLabel: {
    color: "#64748b",

    fontSize: "12px",

    fontWeight: 700,
  },

  summaryMiniValue: {
    color: "#166534",

    fontSize: "19px",

    fontWeight: 900,
  },

  controlsRow: {
    display: "flex",

    gap: "12px",

    alignItems: "flex-end",

    flexWrap: "wrap",
  },

  fieldGroup: {
    width: "260px",

    minWidth: "260px",

    flex: "0 0 260px",
  },

  fieldGroupWide: {
    width: "500px",

    minWidth: "500px",

    maxWidth: "500px",

    flex: "0 0 500px",
  },

  label: {
    display: "block",

    marginBottom: "6px",

    color: "#475569",

    fontSize: "13px",

    fontWeight: 800,
  },

  select: {
    width: "100%",

    height: "42px",

    border: "1px solid #cbd5e1",

    borderRadius: "9px",

    padding: "0 12px",

    background: "#fff",

    color: "#0f172a",

    fontSize: "14px",

    outline: "none",
  },

  addBtn: {
    height: "42px",

    background: "#008080",

    color: "#fff",

    border: "none",

    borderRadius: "9px",

    padding: "0 22px",

    cursor: "pointer",

    fontWeight: 900,

    fontSize: "14px",

    whiteSpace: "nowrap",
  },

  uploadBtn: {
    height: "42px",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    background: "#0f172a",

    color: "#fff",

    border: "none",

    borderRadius: "9px",

    padding: "0 18px",

    cursor: "pointer",

    fontWeight: 900,

    fontSize: "14px",

    whiteSpace: "nowrap",
  },

  uploadedFileName: {
    maxWidth: "270px",

    height: "42px",

    display: "flex",

    alignItems: "center",

    gap: "8px",

    padding: "0 8px 0 12px",

    background: "#f8fafc",

    border: "1px solid #cbd5e1",

    borderRadius: "9px",

    color: "#475569",

    fontSize: "12px",

    fontWeight: 700,

    overflow: "hidden",

    whiteSpace: "nowrap",
  },

  clearDocumentBtn: {
    width: "24px",

    height: "24px",

    minWidth: "24px",

    border: "none",

    borderRadius: "50%",

    background: "#fee2e2",

    color: "#b91c1c",

    cursor: "pointer",

    fontSize: "17px",

    fontWeight: 900,

    lineHeight: "20px",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",
  },

  viewDocumentBtn: {
    height: "42px",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    padding: "0 14px",

    background: "#eff6ff",

    color: "#1d4ed8",

    border: "1px solid #bfdbfe",

    borderRadius: "9px",

    textDecoration: "none",

    fontWeight: 900,

    fontSize: "13px",

    whiteSpace: "nowrap",
  },

  savedDocumentsList: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    flexBasis: "100%",
    marginTop: "2px",
  },

  savedDocumentItem: {
    minHeight: "38px",
    display: "flex",
    alignItems: "center",
    gap: "7px",
    padding: "5px 7px 5px 11px",
    background: "#f8fafc",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
  },

  savedDocumentLink: {
    maxWidth: "230px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#1d4ed8",
    textDecoration: "none",
    fontSize: "12px",
    fontWeight: 800,
  },

  tableCard: {
    background: "#fff",

    border: "1px solid #dce3eb",

    borderRadius: "12px",

    overflow: "hidden",

    boxShadow: "0 5px 16px rgba(15, 23, 42, 0.05)",
  },

  tableHeader: {
    padding: "10px 16px",

    minHeight: "46px",

    borderBottom: "1px solid #dce3eb",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    gap: "12px",
  },

  itemCountBadge: {
    background: "#f1f5f9",

    color: "#334155",

    border: "1px solid #e2e8f0",

    padding: "5px 10px",

    borderRadius: "999px",

    fontWeight: 800,

    fontSize: "12px",
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",

    borderCollapse: "collapse",

    tableLayout: "fixed",

    minWidth: "1500px",
  },

  th: {
    background: "#f3f6fa",

    color: "#172033",

    fontSize: "12px",

    fontWeight: 900,

    padding: "8px 8px",

    height: "34px",

    borderBottom: "1px solid #d9e1ea",

    borderRight: "1px solid #e7edf3",

    textAlign: "left",

    verticalAlign: "middle",

    whiteSpace: "nowrap",
  },

  thCenter: {
    background: "#f3f6fa",

    color: "#172033",

    fontSize: "12px",

    fontWeight: 900,

    padding: "8px 8px",

    height: "34px",

    borderBottom: "1px solid #d9e1ea",

    borderRight: "1px solid #e7edf3",

    textAlign: "center",

    verticalAlign: "middle",

    whiteSpace: "nowrap",
  },

  thRight: {
    background: "#f3f6fa",

    color: "#172033",

    fontSize: "12px",

    fontWeight: 900,

    padding: "8px 8px",

    height: "34px",

    borderBottom: "1px solid #d9e1ea",

    borderRight: "1px solid #e7edf3",

    textAlign: "right",

    verticalAlign: "middle",

    whiteSpace: "nowrap",
  },

  td: {
    padding: "5px 7px",

    borderBottom: "1px solid #e9eef4",

    borderRight: "1px solid #eef2f7",

    verticalAlign: "middle",
  },

  tdCenter: {
    padding: "5px 7px",

    borderBottom: "1px solid #e9eef4",

    borderRight: "1px solid #eef2f7",

    textAlign: "center",

    verticalAlign: "middle",

    fontWeight: 700,
  },

  tdRight: {
    padding: "5px 7px",

    borderBottom: "1px solid #e9eef4",

    borderRight: "1px solid #eef2f7",

    textAlign: "right",

    verticalAlign: "middle",
  },

  itemNameWrap: {
    width: "100%",
    minHeight: "32px",
    padding: "7px 9px",
    boxSizing: "border-box",
    border: "1px solid #dbe3ec",
    borderRadius: "6px",
    background: "#f8fafc",
    color: "#172033",
    fontSize: "13px",
    fontWeight: 700,
    lineHeight: 1.5,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    height: "auto",
  },
  readOnlyInput: {
    width: "100%",

    height: "32px",

    border: "1px solid #dbe3ec",

    borderRadius: "6px",

    padding: "5px 8px",

    background: "#f8fafc",

    color: "#172033",

    fontSize: "13px",

    fontWeight: 700,

    boxSizing: "border-box",
  },

  readOnlyInputCenter: {
    width: "100%",

    height: "32px",

    border: "1px solid #dbe3ec",

    borderRadius: "6px",

    padding: "5px 8px",

    background: "#f8fafc",

    color: "#172033",

    fontSize: "13px",

    fontWeight: 700,

    textAlign: "center",

    boxSizing: "border-box",
  },

  readOnlyInputRight: {
    width: "100%",

    height: "32px",

    border: "1px solid #dbe3ec",

    borderRadius: "6px",

    padding: "5px 8px",

    background: "#f8fafc",

    color: "#172033",

    fontSize: "13px",

    fontWeight: 800,

    textAlign: "right",

    boxSizing: "border-box",
  },

  editInputCenter: {
    width: "100%",

    height: "32px",

    border: "1px solid #94a3b8",

    borderRadius: "6px",

    padding: "5px 8px",

    background: "#fff",

    color: "#0f172a",

    fontSize: "13px",

    fontWeight: 800,

    textAlign: "center",

    outline: "none",

    boxSizing: "border-box",
  },

  editInputRight: {
    width: "100%",

    height: "32px",

    border: "1px solid #94a3b8",

    borderRadius: "6px",

    padding: "5px 8px",

    background: "#fff",

    color: "#0f172a",

    fontSize: "13px",

    fontWeight: 800,

    textAlign: "right",

    outline: "none",

    boxSizing: "border-box",
  },

  diffBadge: {
    display: "inline-block",

    minWidth: "88px",

    padding: "5px 8px",

    borderRadius: "6px",

    fontSize: "12px",

    fontWeight: 900,

    textAlign: "right",
  },

  diffPositive: {
    background: "#ecfdf5",

    color: "#15803d",

    border: "1px solid #bbf7d0",
  },

  diffNegative: {
    background: "#fef2f2",

    color: "#dc2626",

    border: "1px solid #fecaca",
  },

  amountText: {
    color: "#0f172a",

    fontWeight: 900,

    whiteSpace: "nowrap",
  },

  removeBtn: {
    background: "#fee2e2",

    color: "#b91c1c",

    border: "1px solid #fecaca",

    borderRadius: "7px",

    padding: "6px 10px",

    cursor: "pointer",

    fontWeight: 800,

    fontSize: "12px",
  },

  emptyCell: {
    padding: "35px 15px",

    textAlign: "center",

    color: "#64748b",

    fontWeight: 800,
  },

  totalRow: {
    background: "#f0fdf4",
  },

  totalLabelCell: {
    padding: "8px 10px",

    color: "#166534",

    fontSize: "12px",

    fontWeight: 900,
  },

  totalValueCell: {
    padding: "8px 8px",

    color: "#166534",

    fontSize: "12px",

    fontWeight: 900,

    textAlign: "right",

    whiteSpace: "nowrap",
  },

  bottomBar: {
    marginTop: "12px",

    background: "#fff",

    border: "1px solid #dbe3ec",

    borderRadius: "12px",

    padding: "10px 14px",

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    gap: "14px",

    boxShadow: "0 5px 16px rgba(15, 23, 42, 0.05)",
  },

  bottomTotal: {
    display: "flex",

    alignItems: "center",

    gap: "12px",
  },

  bottomTotalLabel: {
    color: "#64748b",

    fontWeight: 800,

    fontSize: "14px",
  },

  bottomTotalValue: {
    color: "#166534",

    fontWeight: 900,

    fontSize: "18px",
  },

  saveBtn: {
    background: "#2563eb",

    color: "#fff",

    border: "none",

    borderRadius: "8px",

    padding: "9px 18px",

    cursor: "pointer",

    fontWeight: 900,

    fontSize: "13px",

    whiteSpace: "nowrap",
  },
};

export default VendorPODetail;
